// src/pages/TemporaryRegistration.tsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Card,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Fade,
  Zoom,
  Grow,
  useTheme,
  useMediaQuery,
  Collapse,
  Alert,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  CameraAlt as CameraIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  FamilyRestroom as FamilyIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  LocalHospital as HospitalIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon,
  Scanner as ScannerIcon,
  Edit as EditIcon,
  Devices as DevicesIcon,
  TabletMac as TabletIcon,
  PhoneAndroid as MobileIcon,
  Tv as TvIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import Webcam from 'react-webcam';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
import { 
  CHECK_PATIENT_AND_FAMILY,
  CREATE_PATIENT
} from '../../graphql/customer.graphql';
import { PROCESS_PRESCRIPTION } from '../../graphql/ai.graphql';
import {
  PROCESS_PRESCRIPTION_AND_SAVE,
//   CREATE_TEMPORARY_REGISTRATION,
  UPDATE_TEMPORARY_REGISTRATION,
  CONVERT_TO_PERMANENT_REGISTRATION,
  GET_TEMPORARY_REGISTRATION_BY_MOBILE,
  SEND_REMINDER_NOTIFICATION,
} from '../../graphql/temporary-registration.graphql';
import { Customer } from '../../types/customer.types';
import { ProcessPrescriptionResponse, ParsedPrescriptionData } from '../../types/prescription.types';
import { PrescriptionTestMatcher } from '../../components/temporary-registration/PrescriptionTestMatcher';
import { ScanOptions } from '../../components/temporary-registration/ScanOptions';
import { SelectedTest,MatchedTestDetails } from '../../types/test.types';
import { PrescriptionPreview } from '../../components/temporary-registration/PrescriptionPreview';
import CorporateStats from '../../components/common/CorporateStats';
import {
  TemporaryRegistration,
  
} from '../../types/temporary-registration.types';

// Types
interface CheckPatientAndFamilyResponse {
  findPatient: Customer | null;
  findFamilyMembers: Customer[];
}

interface GetTempRegistrationsResponse {
  getTemporaryRegistrationByMobile: {
    success: boolean;
    data?: TemporaryRegistration[];
    totalCount?: number;
    error?: string;
  };
}

// Responsive styled components
const ResponsiveContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
  [theme.breakpoints.up('xl')]: {
    maxWidth: '1800px',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '400px',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    zIndex: 0,
    clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)',
    [theme.breakpoints.down('sm')]: {
      height: '300px',
      clipPath: 'polygon(0 0, 100% 0, 100% 90%, 0 100%)',
    },
  },
}));

const ResponsivePaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '32px',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: '24px',
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(2),
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(1.5, 3),
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '16px',
  boxShadow: '0 10px 20px -5px rgba(102, 126, 234, 0.5)',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem',
    borderRadius: '12px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.1rem',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 25px -5px rgba(102, 126, 234, 0.7)',
  },
  '&:disabled': {
    background: 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)',
  },
}));

const LuxuryCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  borderRadius: '24px',
  padding: theme.spacing(1),
  boxShadow: '0 20px 40px -10px rgba(0,0,0,0.4)',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '16px',
    padding: theme.spacing(1.5),
  },
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 30px 50px -10px rgba(0,0,0,0.5)',
  },
}));

const GoldAvatar = styled(Avatar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
  boxShadow: '0 5px 15px rgba(255,215,0,0.3)',
  color: '#1a1a2e',
  fontWeight: 'bold',
  width: 56,
  height: 56,
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
  },
}));

// Updated steps - now only 2 steps
const steps = ['Upload & Verify', 'Confirm Details'];

// Items per page for family members
const ITEMS_PER_PAGE = 10;

// Editable Patient Details Card Component for Step 0
const EditablePatientDetails: React.FC<{
  patientData: {
    name: string;
    age: string;
    gender: string;
    email: string;
    doctorName?: string;
  };
  prescriptionData?: ParsedPrescriptionData | null;
  onUpdate: (field: string, value: string) => void;
  isEditMode: boolean;
  onEditModeChange: (mode: boolean) => void;
  isMobile: boolean;
}> = ({ patientData, prescriptionData, onUpdate, isEditMode, onEditModeChange, isMobile }) => {
  const [localData, setLocalData] = useState(patientData);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setLocalData(patientData);
  }, [patientData]);

  const handleFieldChange = (field: string, value: string) => {
    setLocalData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    Object.entries(localData).forEach(([key, value]) => {
      if (value !== patientData[key as keyof typeof patientData]) {
        onUpdate(key, value);
      }
    });
    onEditModeChange(false);
  };

  const handleCancel = () => {
    setLocalData(patientData);
    onEditModeChange(false);
  };

  return (
    <LuxuryCard sx={{ p: isMobile ? 1.5 : 2, mb: 2 }}>
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        mb={expanded ? 2 : 0}
        sx={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon sx={{ color: '#FFD700' }} />
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ color: 'white' }}>
            Patient Details
          </Typography>
          {prescriptionData?.patientName && !isEditMode && (
            <Chip 
              icon={<CheckIcon />} 
              label="AI Extracted" 
              color="success" 
              size="small" 
              sx={{ ml: 1, height: 20 }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {!isEditMode ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEditModeChange(true);
              }}
              sx={{ color: '#FFD700' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                sx={{ color: '#f44336' }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                sx={{ color: '#4caf50' }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <IconButton size="small" sx={{ color: 'white' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Grid container spacing={isMobile ? 1 : 2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={isEditMode ? localData.name : patientData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              disabled={!isEditMode}
              size={isMobile ? 'small' : 'medium'}
              variant={isEditMode ? 'outlined' : 'filled'}
              InputProps={{
                readOnly: !isEditMode,
                sx: {
                  color: 'white',
                  bgcolor: isEditMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                  '& .MuiFilledInput-root': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                },
              }}
              InputLabelProps={{
                sx: { color: isEditMode ? '#FFD700' : 'rgba(255,255,255,0.7)' },
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Age"
              type="number"
              value={isEditMode ? localData.age : patientData.age}
              onChange={(e) => handleFieldChange('age', e.target.value)}
              disabled={!isEditMode}
              size={isMobile ? 'small' : 'medium'}
              variant={isEditMode ? 'outlined' : 'filled'}
              InputProps={{
                readOnly: !isEditMode,
                sx: {
                  color: 'white',
                  bgcolor: isEditMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                },
              }}
              InputLabelProps={{
                sx: { color: isEditMode ? '#FFD700' : 'rgba(255,255,255,0.7)' },
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'} variant={isEditMode ? 'outlined' : 'filled'}>
              <InputLabel sx={{ color: isEditMode ? '#FFD700' : 'rgba(255,255,255,0.7)' }}>
                Gender
              </InputLabel>
              <Select
                value={isEditMode ? localData.gender : patientData.gender}
                onChange={(e) => handleFieldChange('gender', e.target.value)}
                disabled={!isEditMode}
                label="Gender"
                sx={{
                  color: 'white',
                  bgcolor: isEditMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                  '& .MuiSelect-icon': { color: '#FFD700' },
                }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Doctor Name Field - Editable */}
          {(prescriptionData?.doctorName || isEditMode) && (
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Referring Doctor"
                value={isEditMode ? (localData.doctorName || prescriptionData?.doctorName || '') : (prescriptionData?.doctorName || '')}
                onChange={(e) => handleFieldChange('doctorName', e.target.value)}
                disabled={!isEditMode}
                size={isMobile ? 'small' : 'medium'}
                variant={isEditMode ? 'outlined' : 'filled'}
                InputProps={{
                  readOnly: !isEditMode,
                  startAdornment: prescriptionData?.doctorName && !isEditMode ? (
                    <VerifiedIcon sx={{ color: '#4CAF50', mr: 1, fontSize: 18 }} />
                  ) : null,
                  sx: {
                    color: 'white',
                    bgcolor: isEditMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                  },
                }}
                InputLabelProps={{
                  sx: { color: isEditMode ? '#FFD700' : 'rgba(255,255,255,0.7)' },
                }}
              />
            </Grid>
          )}
        </Grid>

        {isEditMode && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Edit the fields above and click save to update patient information.
          </Alert>
        )}
      </Collapse>
    </LuxuryCard>
  );
};

// Device Info Component
const DeviceInfo: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isKiosk = window.innerWidth >= 1920 && window.innerHeight >= 1080;

  let deviceType = 'Desktop';
  let DeviceIcon = DevicesIcon;
  
  if (isKiosk) {
    deviceType = 'Kiosk';
    DeviceIcon = TvIcon;
  } else if (isMobile) {
    deviceType = 'Mobile';
    DeviceIcon = MobileIcon;
  } else if (isTablet) {
    deviceType = 'Tablet';
    DeviceIcon = TabletIcon;
  }

  return (
    <Chip
      icon={<DeviceIcon />}
      label={`${deviceType} Mode`}
      size="small"
      sx={{
        bgcolor: 'rgba(255,255,255,0.1)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.2)',
        ml: 1,
      }}
    />
  );
};

// Recent Registrations Dialog
const RecentRegistrationsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  registrations: TemporaryRegistration[];
  onSelect: (reg: TemporaryRegistration) => void;
  onSendReminder: (id: number) => void;
}> = ({ open, onClose, registrations, onSelect, onSendReminder }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Recent Incomplete Registrations</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={2}>
        {registrations.map((reg) => (
          <Grid size={{ xs:12 }}  key={reg.id}>
            <Card sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {reg.registrationNo}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {reg.patientName || 'Unknown'} • {reg.mobileNumber || 'No mobile'} • Created: {new Date(reg.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Expires: {reg.expiresAt ? new Date(reg.expiresAt).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Tooltip title="Continue Registration">
                  <IconButton onClick={() => onSelect(reg)} color="primary">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Send Reminder">
                  <IconButton onClick={() => onSendReminder(reg.id)} color="secondary">
                    <NotificationsIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

// Step Icon Component
const StepIcon: React.FC<{ active?: boolean; completed?: boolean; icon: React.ReactNode }> = ({ active, completed, icon }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        width: isMobile ? 32 : 40,
        height: isMobile ? 32 : 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: active 
          ? 'linear-gradient(135deg, #FFD700, #FFA500)'
          : completed
          ? 'linear-gradient(135deg, #4CAF50, #45a049)'
          : 'rgba(255,255,255,0.1)',
        color: active || completed ? '#1a1a2e' : 'rgba(255,255,255,0.5)',
        boxShadow: active ? '0 0 20px #FFD700' : 'none',
        fontSize: isMobile ? '0.875rem' : '1rem',
      }}
    >
      {icon}
    </Box>
  );
};

export const SelfRegistration: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isKiosk = window.innerWidth >= 1920 && window.innerHeight >= 1080;

  const [activeStep, setActiveStep] = useState(0);
  const [mobileNumber, setMobileNumber] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [processingAI, setProcessingAI] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [isScanning, setIsScanning] = useState(false);
  const [scanOptions, setScanOptions] = useState({
    useCamera: true,
    useUpload: false,
    useScanner: false,
    autoProcess: true,
    extractMobile: true,
    scannerSettings: {
      device: '',
      resolution: 300,
      colorMode: 'Color' as 'Color' | 'Gray' | 'Lineart',
      pageSize: 'A4' as 'A4' | 'Letter' | 'Legal',
      source: 'ADF' as 'ADF' | 'Flatbed',
      duplex: false,
    },
  });

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [prescriptionData, setPrescriptionData] = useState<ParsedPrescriptionData | null>(null);
  const [_showMobileInput] = useState(false);
  const [newPatient, setNewPatient] = useState({
    name: '',
    age: '',
    gender: 'Male',
    email: '',
    doctorName: '',
  });
  
  // Temporary Registration state
  const [tempRegistration, setTempRegistration] = useState<TemporaryRegistration | null>(null);
  const [recentTempRegistrations, setRecentTempRegistrations] = useState<TemporaryRegistration[]>([]);
  const [showRecentRegistrations, setShowRecentRegistrations] = useState(false);
  
  // Manual edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Test selection state
  const [selectedTestIds, setSelectedTestIds] = useState<number[]>([]);
  const [selectedTests, setSelectedTests] = useState<SelectedTest[]>([]);
  
  // Family member related state
  const [familyMembers, setFamilyMembers] = useState<Customer[]>([]);
  const [selectedMember, setSelectedMember] = useState<Customer | null>(null);
  const [showFamilySelector, setShowFamilySelector] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [tempSelectedMember, setTempSelectedMember] = useState<Customer | null>(null);
  
  // Manual step progression state
  const [canProceed, setCanProceed] = useState(false);
  
  // Pagination and search for large family lists
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('all');

  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // GraphQL hooks with proper typing
  const [checkPatientAndFamily] = useLazyQuery<CheckPatientAndFamilyResponse>(CHECK_PATIENT_AND_FAMILY);
  const [createCustomer] = useMutation<{ createPatient: Customer }>(CREATE_PATIENT);
  const [processPrescription] = useMutation<ProcessPrescriptionResponse>(PROCESS_PRESCRIPTION);
  const [processPrescriptionAndSave] = useMutation<{ processPrescriptionAndSave: { success: boolean; data: TemporaryRegistration } }>(PROCESS_PRESCRIPTION_AND_SAVE);
  const [updateTemporaryRegistration] = useMutation<{ updateTemporaryRegistration: { success: boolean; data: TemporaryRegistration } }>(UPDATE_TEMPORARY_REGISTRATION);
  const [convertToPermanent] = useMutation<{ convertToPermanentRegistration: { success: boolean; registrationId: number; registrationNo: string; error?: string } }>(CONVERT_TO_PERMANENT_REGISTRATION);
  const [getTempRegistrationsByMobile] = useLazyQuery<GetTempRegistrationsResponse>(GET_TEMPORARY_REGISTRATION_BY_MOBILE);
  const [sendReminder] = useMutation<{ sendReminderNotification: { success: boolean; message: string; sentVia: string; error?: string } }>(SEND_REMINDER_NOTIFICATION);

  // Load recent temporary registrations on mount
  useEffect(() => {
    loadRecentRegistrations();
  }, []);

  const loadRecentRegistrations = async () => {
    try {
      const tempRegs = localStorage.getItem('recentTempRegistrations');
      if (tempRegs) {
        setRecentTempRegistrations(JSON.parse(tempRegs));
      }
    } catch (error) {
      console.error('Error loading recent registrations:', error);
    }
  };

  const saveRecentRegistration = (registration: TemporaryRegistration) => {
    try {
      const updated = [registration, ...recentTempRegistrations.filter(r => r.id !== registration.id)].slice(0, 5);
      setRecentTempRegistrations(updated);
      localStorage.setItem('recentTempRegistrations', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving recent registration:', error);
    }
  };

  const handleScanOptionChange = (key: string, value: any) => {
    if (key === 'scannerSettings') {
      setScanOptions(prev => ({
        ...prev,
        scannerSettings: {
          ...prev.scannerSettings,
          ...value
        }
      }));
    } else {
      setScanOptions(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setPreviewImage(imageSrc);
      setShowPreview(true);
      setShowCamera(false);
      if (scanOptions.autoProcess) {
        processPrescriptionImage(imageSrc);
      }
    } else {
      enqueueSnackbar('Failed to capture image. Please try again.', { variant: 'error' });
    }
  }, [webcamRef, scanOptions.autoProcess]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      enqueueSnackbar('File too large. Max size is 10MB', { variant: 'error' });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Invalid file type. Please upload JPG, PNG or PDF', { variant: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target?.result as string;
      if (imageData) {
        setPreviewImage(imageData);
        setShowPreview(true);
        if (scanOptions.autoProcess) {
          processPrescriptionImage(imageData);
        }
      }
    };
    reader.onerror = () => {
      enqueueSnackbar('Error reading file', { variant: 'error' });
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    setIsScanning(true);
    try {
      const response = await fetch('http://localhost:3000/api/scanners/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device: scanOptions.scannerSettings?.device,
          options: {
            resolution: scanOptions.scannerSettings?.resolution || 200,
            colorMode: scanOptions.scannerSettings?.colorMode || 'Color',
            source: scanOptions.scannerSettings?.source || 'Flatbed',
          }
        }),
      });

      const result = await response.json();
      
      if (!response.ok) throw new Error(result.message || 'Scan failed');
      
      if (result.success && result.data && Array.isArray(result.data) && result.data.length > 0) {
        setPreviewImage(result.data[0]);
        setShowPreview(true);
        
        for (const image of result.data) {
          await processPrescriptionImage(image);
        }
        
        enqueueSnackbar(result.message || `Scanned ${result.data.length} page(s) successfully!`, { variant: 'success' });
      } else {
        throw new Error(result.message || 'No images were scanned');
      }
    } catch (error) {
      console.error('Scan error:', error);
      enqueueSnackbar('Failed to scan document. Please check scanner connection.', { variant: 'error' });
    } finally {
      setIsScanning(false);
    }
  };

  // Handle patient data update from edit component
  const handlePatientUpdate = (field: string, value: string) => {
    setNewPatient(prev => ({ ...prev, [field]: value }));
    
    // Also update prescriptionData if it's doctorName
    if (field === 'doctorName' && prescriptionData) {
      setPrescriptionData({
        ...prescriptionData,
        doctorName: value
      });
    }
    
    // Update temporary registration if exists
    if (tempRegistration) {
      updateExistingTempRegistration({
        ...tempRegistration,
        patientName: field === 'name' ? value : tempRegistration.patientName,
        age: field === 'age' ? parseInt(value) : tempRegistration.age,
        gender: field === 'gender' ? value as any : tempRegistration.gender,
        doctorName: field === 'doctorName' ? value : tempRegistration.doctorName,
      });
    }
    
    enqueueSnackbar(`${field === 'doctorName' ? 'Doctor name' : 'Patient ' + field} updated`, { 
      variant: 'success' 
    });
  };

  const updateExistingTempRegistration = async (updatedData: Partial<TemporaryRegistration>) => {
    if (!tempRegistration) return;

    try {
      const { data } = await updateTemporaryRegistration({
        variables: {
          input: {
            id: tempRegistration.id,
            patientName: updatedData.patientName,
            age: updatedData.age,
            gender: updatedData.gender,
            doctorName: updatedData.doctorName,
            selectedTests: selectedTests,
          }
        }
      });

      if (data?.updateTemporaryRegistration?.success) {
        setTempRegistration(data.updateTemporaryRegistration.data);
        saveRecentRegistration(data.updateTemporaryRegistration.data);
      }
    } catch (error) {
      console.error('Error updating temporary registration:', error);
    }
  };

  const processPrescriptionImage = async (imageData: string) => {
    if (!imageData) {
      enqueueSnackbar('No image data to process', { variant: 'error' });
      return;
    }

    setProcessingAI(true);
    setUploadProgress(0);
    
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      // First process with AI
      const { data: processData } = await processPrescription({
        variables: { image: imageData },
      });

      clearInterval(interval);
      setUploadProgress(100);

      if (processData?.processPrescription?.success) {
        console.log('Prescription data received:', processData);
        const parsed = processData.processPrescription.parsedData;
        setPrescriptionData(parsed);
        
        // Create selected tests array with proper typing
       const tests: SelectedTest[] = parsed.tests?.map((test: any) => {
  // Ensure all required fields have default values
  const testId = test.matchedTest?.TestId || 0;
  const testName = test.name || '';
  const amount = test.matchedTest?.Amount || 0;
  const confidence = test.confidence || processData.processPrescription.confidence || 0;
  
  return {
    testId,
    testName,
    amount,
    quantity: 1,
    discount: 0,
    aiConfidence: confidence,
    manuallyAdded: false,
  };
}) || [];

        setSelectedTests(tests);
        
        if (parsed.tests && parsed.tests.length > 0) {
          const matchedIds = parsed.tests
            .filter(test => test.matchedTest)
            .map(test => test.matchedTest!.TestId);
          setSelectedTestIds(matchedIds);
        }
        
        setNewPatient({
          name: parsed.patientName || '',
          age: parsed.age?.toString() || '',
          gender: parsed.gender === 'M' ? 'Male' : parsed.gender === 'F' ? 'Female' : 'Male',
          email: '',
          doctorName: parsed.doctorName || '',
        });

        // Create temporary registration
        const { data: tempData } = await processPrescriptionAndSave({
          variables: {
            image: imageData,
            expiresInMinutes: 1440, // 24 hours
          }
        });

        if (tempData?.processPrescriptionAndSave?.success) {
          const tempReg = tempData.processPrescriptionAndSave.data;
          setTempRegistration(tempReg);
          saveRecentRegistration(tempReg);
          enqueueSnackbar(`Temporary registration created: ${tempReg.registrationNo}`, { variant: 'success' });
        }

        // Enable the Next button after successful processing
        setCanProceed(true);

        if (scanOptions.extractMobile && parsed.mobileNumber) {
          setMobileNumber(parsed.mobileNumber);
          enqueueSnackbar(`Mobile number ${parsed.mobileNumber} found.`, { variant: 'info' });
        } else {
          if (parsed.mobileNumber) setMobileNumber(parsed.mobileNumber);
          enqueueSnackbar(
            parsed.mobileNumber 
              ? 'Mobile number found. Please verify to continue.' 
              : 'Mobile number not found. Please enter manually.',
            { variant: 'info' 
          });
        }
      } else {
        enqueueSnackbar('Failed to process prescription', { variant: 'error' });
      }
    } catch (error) {
      clearInterval(interval);
      console.error('AI processing error:', error);
      enqueueSnackbar('Failed to process prescription. Please try again.', { variant: 'error' });
    } finally {
      setProcessingAI(false);
    }
  };

  const handleManualProcess = () => {
    if (previewImage) {
      processPrescriptionImage(previewImage);
    }
  };

  // Add Next Step handler
  const handleNextStep = () => {
    if (mobileNumber.length !== 10) {
      enqueueSnackbar('Please enter a valid 10-digit mobile number', { variant: 'warning' });
      return;
    }
    setActiveStep(1);
  };

  const handleSelectMember = (member: Customer) => {
    setTempSelectedMember(member); // Just set temporary selection
  };

  const handleConfirmMemberSelection = () => {
    if (tempSelectedMember) {
      setSelectedMember(tempSelectedMember);
      setCustomer(tempSelectedMember);
      setNewPatient({
        name: tempSelectedMember.CustomerName || newPatient.name,
        age: tempSelectedMember.Age?.toString() || newPatient.age,
        gender: tempSelectedMember.Gender || newPatient.gender,
        email: tempSelectedMember.EmailId || '',
        doctorName: prescriptionData?.doctorName || '',
      });
      setShowFamilySelector(false); // Hide selector only after confirmation
      enqueueSnackbar(`Selected: ${tempSelectedMember.CustomerName}`, { variant: 'success' });
    } else {
      enqueueSnackbar('Please select a family member', { variant: 'warning' });
    }
  };

  const handleAddFamilyMember = async (memberData: {
    CustomerName: string;
    Age: number;
    Gender: string;
    MobileNumber: string;
    Relationship: string;
  }) => {
    try {
      const familyHead = familyMembers.find(m => m.IsFamilyHead) || selectedMember;
      
      if (!familyHead) {
        enqueueSnackbar('Cannot add member: No family head found', { variant: 'error' });
        return;
      }

      let genderValue = memberData.Gender;
      if (genderValue === 'M') genderValue = 'Male';
      else if (genderValue === 'F') genderValue = 'Female';

      const { data } = await createCustomer({
        variables: {
          input: {
            mobile: mobileNumber,
            name: memberData.CustomerName,
            age: memberData.Age,
            gender: genderValue,
            relationship: memberData.Relationship,
            familyHeadId: familyHead.CustomerId,
            isFamilyHead: false,
          }
        }
      });

      if (data?.createPatient) {
        const { data: updatedData } = await checkPatientAndFamily({ variables: { mobile: mobileNumber } });
        
        if (updatedData?.findFamilyMembers) {
          setFamilyMembers(updatedData.findFamilyMembers);
        }
        
        enqueueSnackbar('Family member added successfully!', { variant: 'success' });
        setShowAddMemberDialog(false);
      }
    } catch (error) {
      console.error('Error adding family member:', error);
      enqueueSnackbar('Failed to add family member', { variant: 'error' });
    }
  };

  const handleContinueAsGuest = () => {
    setShowFamilySelector(false);
    setTempSelectedMember(null); // Clear temporary selection
    setSelectedMember(null);
    enqueueSnackbar('Continuing as guest', { variant: 'info' });
  };

  const handleMobileSubmit = async (num?: string) => {
    const mobileToCheck = num || mobileNumber;
    
    if (mobileToCheck.length !== 10) {
      enqueueSnackbar('Please enter a valid 10-digit mobile number', { variant: 'warning' });
      return;
    }

    setCheckingMobile(true);

    try {
      // Check for existing temporary registrations
      const { data: tempData } = await getTempRegistrationsByMobile({
        variables: { mobile: mobileToCheck }
      });

      if (tempData?.getTemporaryRegistrationByMobile?.success && 
          tempData.getTemporaryRegistrationByMobile.data && 
          tempData.getTemporaryRegistrationByMobile.data.length > 0) {
        setRecentTempRegistrations(tempData.getTemporaryRegistrationByMobile.data);
        setShowRecentRegistrations(true);
      }

      // Check for existing patients
      const { data } = await checkPatientAndFamily({ variables: { mobile: mobileToCheck } });
      
      const patient = data?.findPatient;
      const family = data?.findFamilyMembers || [];

      setMobileNumber(mobileToCheck);

      if (!patient && family.length === 0) {
        // New patient - stay on step 0
        setCustomer(null);
        setShowFamilySelector(false);
        setSelectedMember(null);
        setTempSelectedMember(null);
        setFamilyMembers([]);
        enqueueSnackbar('Mobile number verified. New patient.', { variant: 'success' });
      } else if (patient && family.length === 0) {
        // Existing patient with no family
        setCustomer(patient);
        setSelectedMember(patient);
        setTempSelectedMember(patient);
        setShowFamilySelector(false);
        setFamilyMembers([]);
        setNewPatient({
          name: patient.CustomerName || newPatient.name,
          age: patient.Age?.toString() || newPatient.age,
          gender: patient.Gender || newPatient.gender,
          email: patient.EmailId || '',
          doctorName: prescriptionData?.doctorName || '',
        });
        enqueueSnackbar(`Welcome back, ${patient.CustomerName}!`, { variant: 'success' });
      } else if (family.length > 0) {
        // Family members exist
        setFamilyMembers(family);
        
        if (patient) {
          setTempSelectedMember(patient);
          setSelectedMember(patient);
          setCustomer(patient);
          setNewPatient({
            name: patient.CustomerName || newPatient.name,
            age: patient.Age?.toString() || newPatient.age,
            gender: patient.Gender || newPatient.gender,
            email: patient.EmailId || '',
            doctorName: prescriptionData?.doctorName || '',
          });
        } else {
          setTempSelectedMember(null);
          setSelectedMember(null);
          setCustomer(null);
        }
        
        setShowFamilySelector(true);
        enqueueSnackbar(
          `Found ${family.length} family member${family.length > 1 ? 's' : ''}. Please select one to continue.`, 
          { variant: 'info' }
        );
      }
    } catch (error) {
      console.error('Error checking patient and family:', error);
      enqueueSnackbar('Error verifying mobile number', { variant: 'error' });
    } finally {
      setCheckingMobile(false);
    }
  };

 const handleTestSelect = (test: MatchedTestDetails) => {
  setSelectedTestIds(prev => {
    const newIds = prev.includes(test.TestId)
      ? prev.filter(id => id !== test.TestId)
      : [...prev, test.TestId];
    
    // Update selected tests - ensure all required fields have values
    const updatedTests: SelectedTest[] = newIds.map(id => ({
      testId: id,
      testName: test.TestName || '', // Ensure string, not undefined
      amount: test.Amount || 0, // Ensure number, not undefined
      quantity: 1,
      discount: 0,
      aiConfidence: test.aiConfidence || 0,
      manuallyAdded: true,
    }));
    
    setSelectedTests(updatedTests);
    
    // Update temporary registration
    if (tempRegistration) {
      updateExistingTempRegistration({ selectedTests: updatedTests });
    }
    
    return newIds;
  });
};

  const handleConfirmTests = async () => {
    if (!tempRegistration) {
      enqueueSnackbar('No temporary registration found', { variant: 'error' });
      return;
    }

    try {
      // Convert to permanent registration
      const { data } = await convertToPermanent({
        variables: {
          id: tempRegistration.id,
          customerId: customer?.CustomerId,
        }
      });

      if (data?.convertToPermanentRegistration?.success) {
        enqueueSnackbar('Registration completed successfully!', { variant: 'success' });
        navigate('/quick-registration', {
          state: {
            patient: customer,
            selectedTests: selectedTestIds,
            prescriptionData: prescriptionData,
            registrationNo: data.convertToPermanentRegistration.registrationNo,
          }
        });
      } else {
        enqueueSnackbar(data?.convertToPermanentRegistration?.error || 'Failed to complete registration', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error converting to permanent:', error);
      enqueueSnackbar('Failed to complete registration', { variant: 'error' });
    }
  };

  const handleCreateCustomer = async () => {
    if (!newPatient.name || !newPatient.age) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }

    try {
      let genderValue = newPatient.gender;
      if (genderValue === 'M') genderValue = 'Male';
      else if (genderValue === 'F') genderValue = 'Female';
      
      const { data } = await createCustomer({
        variables: {
          input: {
            mobile: mobileNumber,
            name: newPatient.name,
            age: parseInt(newPatient.age),
            gender: genderValue,
            email: newPatient.email || undefined,
          }
        }
      });

      if (data?.createPatient) {
        setCustomer(data.createPatient);
        enqueueSnackbar('Registration successful!', { variant: 'success' });
        
        // Convert temporary to permanent
        if (tempRegistration) {
          await handleConfirmTests();
        } else {
          navigate('/quick-registration', {
            state: {
              patient: data.createPatient,
              selectedTests: selectedTestIds,
              prescriptionData: prescriptionData
            }
          });
        }
      }
    } catch (error: any) {
      console.error('Error creating customer:', error);
      enqueueSnackbar(error.message || 'Failed to create patient', { variant: 'error' });
    }
  };

  const handleContinueWithCustomer = () => {
    if (tempRegistration) {
      handleConfirmTests();
    } else {
      navigate('/quick-registration', {
        state: {
          patient: customer,
          selectedTests: selectedTestIds,
          prescriptionData: prescriptionData
        }
      });
    }
  };

  const handleSendReminder = async (id: number) => {
    try {
      const { data } = await sendReminder({
        variables: { id }
      });

      if (data?.sendReminderNotification?.success) {
        enqueueSnackbar(`Reminder sent via ${data.sendReminderNotification.sentVia}`, { variant: 'success' });
      } else {
        enqueueSnackbar(data?.sendReminderNotification?.error || 'Failed to send reminder', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      enqueueSnackbar('Failed to send reminder', { variant: 'error' });
    }
  };

  const getUniqueRelationships = () => {
    const relationships = familyMembers
      .map(m => m.Relationship)
      .filter((r): r is string => !!r)
      .filter((r, i, self) => self.indexOf(r) === i);
    return ['all', ...relationships];
  };

  const filteredFamilyMembers = familyMembers.filter(member => {
    const matchesSearch = searchTerm === '' || 
      member.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.MobileNumber?.includes(searchTerm);
    
    const matchesRelationship = relationshipFilter === 'all' || member.Relationship === relationshipFilter;
    
    return matchesSearch && matchesRelationship;
  });

  const paginatedMembers = filteredFamilyMembers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => setCurrentPage(1), [searchTerm, relationshipFilter]);

  // const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
  //   setCurrentPage(value);
  // };

  const [newFamilyMember, setNewFamilyMember] = useState({ relationship: 'Other' });

  const handleRelationshipChange = (event: SelectChangeEvent) => {
    setNewFamilyMember({ ...newFamilyMember, relationship: event.target.value });
  };

  const kioskStyles = {
    ...(isKiosk && {
      '& .MuiButton-root': {
        fontSize: '1.5rem',
        padding: '20px 40px',
      },
      '& .MuiTypography-root': {
        fontSize: '1.2rem',
      },
    }),
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0: // Combined Upload & Verify Step
        return (
          <Fade in={true} timeout={800}>
            <Box sx={{ mt: isMobile ? 1 : 2 }}>
              {/* Scan Options Row */}
              <Grid container spacing={1}>
                {/* Left Column - Scan Options */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <ScanOptions 
                    options={scanOptions} 
                    onChange={handleScanOptionChange} 
                    onScan={handleScan}
                    isScanning={isScanning}
                  />

                  {/* Camera Option Button */}
                  {scanOptions.useCamera && (
                    <Box sx={{ mb: 2 }}>
                      <Paper
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '24px',
                          boxShadow: '0 20px 30px -10px rgba(102, 126, 234, 0.5)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 30px 40px -10px rgba(102, 126, 234, 0.7)',
                          },
                        }}
                        onClick={() => setShowCamera(true)}
                      >
                        <CameraIcon sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                          Take Photo
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Use camera to capture prescription
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Upload Option Button */}
                  {scanOptions.useUpload && (
                    <Box sx={{ mb: 2 }}>
                      <Paper
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          borderRadius: '24px',
                          boxShadow: '0 20px 30px -10px rgba(255,215,0,0.5)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 30px 40px -10px rgba(255,215,0,0.7)',
                          },
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <UploadIcon sx={{ fontSize: 48, color: '#1a1a2e', mb: 1 }} />
                        <Typography variant="h6" sx={{ color: '#1a1a2e', fontWeight: 600 }}>
                          Upload File
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a1a2e', opacity: 0.8 }}>
                          JPG, PNG or PDF (max 10MB)
                        </Typography>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* Scanner Option */}
                  {scanOptions.useScanner && (
                    <Box sx={{ mb: 2 }}>
                      <Paper
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                          borderRadius: '24px',
                          boxShadow: '0 20px 30px -10px rgba(76,175,80,0.5)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 30px 40px -10px rgba(76,175,80,0.7)',
                          },
                        }}
                        onClick={handleScan}
                      >
                        <ScannerIcon sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                          Scan Document
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Use scanner to capture prescription
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Grid>

                {/* Right Column - Prescription Preview */}
                <Grid size={{ xs: 12, md: 9 }}>
                  {showPreview && previewImage ? (
                    <PrescriptionPreview
                      imageUrl={previewImage}
                      onClose={() => setShowPreview(false)}
                      onDownload={() => {
                        const link = document.createElement('a');
                        link.href = previewImage;
                        link.download = 'prescription.jpg';
                        link.click();
                      }}
                      extractedData={prescriptionData}
                    />
                  ) : (
                    <Paper
                      sx={{
                        height: '100%',
                        minHeight: isMobile ? 300 : 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, rgba(26,26,46,0.9) 0%, rgba(22,33,62,0.9) 100%)',
                        borderRadius: isMobile ? '16px' : '24px',
                        border: '2px dashed rgba(255,215,0,0.3)',
                      }}
                    >
                      <Box textAlign="center" p={isMobile ? 2 : 3}>
                        <CameraIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'rgba(255,215,0,0.5)', mb: 2 }} />
                        <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          No Prescription Selected
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          Choose an option on the left
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Grid>
              </Grid>

              {/* Processing Status */}
              {processingAI && (
                <Grow in={true}>
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress}
                      sx={{ borderRadius: 2, height: 8 }}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', display: 'block', mt: 1 }}>
                      {uploadProgress}% - Processing...
                    </Typography>
                  </Box>
                </Grow>
              )}

              {/* Manual Process Button */}
              {showPreview && !scanOptions.autoProcess && !processingAI && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <GradientButton onClick={handleManualProcess} fullWidth>
                    Process Prescription
                  </GradientButton>
                </Box>
              )}

              {/* Mobile Number Input - Single Row with Verify Button */}
              <Box sx={{ mt: 0, mb: 0 }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ color: 'white', mb: 2 }}>
                  Mobile Number Verification
                  {prescriptionData?.mobileNumber && (
                    <Chip icon={<CheckIcon />} label="Found" color="success" size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter 10-digit mobile number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: '#667eea' }} />
                          </InputAdornment>
                        ),
                      }}
                      disabled={checkingMobile}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <GradientButton
                      fullWidth
                      onClick={() => handleMobileSubmit()}
                      disabled={mobileNumber.length !== 10 || checkingMobile}
                      size={isMobile ? 'medium' : 'large'}
                      sx={{ height: isMobile ? 'auto' : 56 }}
                    >
                      {checkingMobile ? 'Checking...' : 'Verify'}
                    </GradientButton>
                  </Grid>
                </Grid>

                {checkingMobile && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>

              {/* Family Member Selector */}
              {showFamilySelector && familyMembers.length > 0 && (
                <Zoom in={true}>
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <ResponsivePaper>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FamilyIcon sx={{ color: '#667eea' }} />
                          Family Members ({familyMembers.length})
                        </Typography>
                        <GradientButton
                          startIcon={<AddIcon />}
                          onClick={() => setShowAddMemberDialog(true)}
                          size="small"
                          sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem', py: 1 }}
                        >
                          Add Member
                        </GradientButton>
                      </Box>

                      {/* Search and Filter */}
                      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name or mobile"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                              startAdornment: <SearchIcon sx={{ color: '#667eea', mr: 1 }} />,
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Relationship</InputLabel>
                            <Select
                              value={relationshipFilter}
                              label="Relationship"
                              onChange={(e: SelectChangeEvent) => setRelationshipFilter(e.target.value)}
                            >
                              {getUniqueRelationships().map(rel => (
                                <MenuItem key={rel} value={rel}>
                                  {rel === 'all' ? 'All' : rel}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          {paginatedMembers.length === 0 ? (
                            <Typography variant="caption" sx={{ color: 'text.secondary', p: 1 }}>
                              No members found
                            </Typography>
                          ) : (
                            <FormControl fullWidth size="small" sx={{ mb: 0 }}>
                              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Select Family Member</InputLabel>
                              <Select
                                value={tempSelectedMember?.CustomerId || ''}
                                onChange={(e) => {
                                  const member = familyMembers.find(m => m.CustomerId === e.target.value);
                                  if (member) handleSelectMember(member);
                                }}
                                label="Select Family Member"
                                sx={{
                                  color: 'white',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,215,0,0.3)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#FFD700',
                                  },
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      bgcolor: '#1a1a2e',
                                      '& .MuiMenuItem-root': {
                                        color: 'white',
                                      },
                                      '& .Mui-selected': {
                                        bgcolor: '#FFD700 !important',
                                        color: '#1a1a2e !important',
                                        '&:hover': {
                                          bgcolor: '#FFD700 !important',
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {familyMembers.map((member) => (
                                  <MenuItem 
                                    key={member.CustomerId} 
                                    value={member.CustomerId}
                                  >
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Avatar sx={{ 
                                        width: 24, 
                                        height: 24, 
                                        bgcolor: 'rgba(255,215,0,0.2)',
                                        color: '#FFD700'
                                      }}>
                                        {member.Gender?.toLowerCase() === 'male' ? <MaleIcon sx={{ fontSize: 14 }} /> :
                                         member.Gender?.toLowerCase() === 'female' ? <FemaleIcon sx={{ fontSize: 14 }} /> :
                                         <PersonIcon sx={{ fontSize: 14 }} />}
                                      </Avatar>
                                      <Box>
                                        <Typography variant="body2">
                                          {member.CustomerName}
                                          {member.IsFamilyHead && ' 👑'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                          {member.Age || 'N/A'} • {member.Relationship || 'N/A'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button variant="outlined" onClick={handleContinueAsGuest} size={isMobile ? 'small' : 'medium'}>
                          Continue as Guest
                        </Button>
                        <GradientButton 
                          onClick={handleConfirmMemberSelection}
                          disabled={!tempSelectedMember}
                          size={isMobile ? 'small' : 'medium'}
                        >
                          Continue with Selected Member
                        </GradientButton>
                      </Box>
                    </ResponsivePaper>
                  </Box>
                </Zoom>
              )}

              {/* Editable Patient Details - Shows after AI processing */}
              {canProceed && (
                <EditablePatientDetails
                  patientData={newPatient}
                  prescriptionData={prescriptionData}
                  onUpdate={handlePatientUpdate}
                  isEditMode={isEditMode}
                  onEditModeChange={setIsEditMode}
                  isMobile={isMobile}
                />
              )}

              {/* Next Button - Only shown after AI processing */}
              {canProceed && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <GradientButton
                    onClick={handleNextStep}
                    disabled={mobileNumber.length !== 10}
                    size="large"
                    sx={{ minWidth: 200 }}
                  >
                    Next Step →
                  </GradientButton>
                </Box>
              )}
            </Box>
          </Fade>
        );

      case 1: // Patient Details & Test Selection Step
        return (
          <Fade in={true} timeout={800}>
            <Box sx={{ mt: isMobile ? 1 : 2 }}>
              {/* Patient Details Summary Card (Read-only) */}
              <Zoom in={true}>
                <LuxuryCard sx={{ p: isMobile ? 1.5 : 2, mb: 2 }}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white', mb: 2 }}>
                    <PersonIcon sx={{ color: '#FFD700' }} />
                    Patient Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Full Name</Typography>
                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>{newPatient.name}</Typography>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Age</Typography>
                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>{newPatient.age}</Typography>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Gender</Typography>
                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>{newPatient.gender}</Typography>
                      </Paper>
                    </Grid>
                    {prescriptionData?.doctorName && (
                      <Grid size={{ xs: 12 }}>
                        <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Referring Doctor</Typography>
                          <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>{prescriptionData.doctorName}</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </LuxuryCard>
              </Zoom>

              {/* Detected Tests */}
              {prescriptionData?.tests && prescriptionData.tests.length > 0 && (
                <Zoom in={true}>
                  <Box sx={{ mt: 2 }}>
                    <PrescriptionTestMatcher
                      extractedTests={prescriptionData.tests}
                      unmatchedTests={prescriptionData.unmatchedTests || []}
                      selectedTests={selectedTestIds}
                      onTestSelect={handleTestSelect}
                      onConfirmSelection={handleConfirmTests}
                    />
                  </Box>
                </Zoom>
              )}

              {/* Action Buttons */}
              <Box sx={{ mt: 2 }}>
                {customer ? (
                  <GradientButton fullWidth onClick={handleContinueWithCustomer} size={isMobile ? 'medium' : 'large'}>
                    Continue with Existing Patient
                  </GradientButton>
                ) : (
                  <GradientButton fullWidth onClick={handleCreateCustomer} disabled={!newPatient.name || !newPatient.age} size={isMobile ? 'medium' : 'large'}>
                    Register New Patient & Continue
                  </GradientButton>
                )}
              </Box>
            </Box>
          </Fade>
        );

      default:
        return null;
    }
  };

  return (
    <ResponsiveContainer maxWidth="xl" sx={kioskStyles}>
      <Box sx={{ position: 'relative', zIndex: 1, py: isMobile ? 1 : 2 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 1,
          gap: 1,
          color: 'white',
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: isMobile ? 40 : 60, height: isMobile ? 40 : 60, background: 'linear-gradient(135deg, #FFD700, #FFA500)' }}>
              <HospitalIcon sx={{ fontSize: isMobile ? 24 : 40, color: '#1a1a2e' }} />
            </Avatar>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700 }}>
                DIAGNOSTIC LAB
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Corporate Health Solutions
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <GoldAvatar sx={{ width: isMobile ? 40 : 50, height: isMobile ? 40 : 50 }}>
              <PersonIcon />
            </GoldAvatar>
            <Box>
              <Typography variant={isMobile ? 'body1' : 'h5'} sx={{ fontWeight: 700 }}>
                Patient Portal
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                AI-Powered Extraction
              </Typography>
            </Box>
            <DeviceInfo />
          </Box>

          <Box display="flex" gap={1}>
            <Chip icon={<SecurityIcon />} label="HIPAA" size={isMobile ? 'small' : 'medium'} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Chip icon={<SpeedIcon />} label="AI" size={isMobile ? 'small' : 'medium'} sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white' }} />
          </Box>
        </Box>

        {/* Main Content */}
        <ResponsivePaper>
          <CorporateStats />
          
          <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mb: 2 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel StepIconComponent={() => (
                  <StepIcon 
                    active={activeStep === index}
                    completed={activeStep > index}
                    icon={activeStep > index ? <CheckIcon /> : index + 1}
                  />
                )}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {getStepContent(activeStep)}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="text" onClick={() => navigate('/employee-login')} size={isMobile ? 'small' : 'medium'}>
              Employee Login
            </Button>
          </Box>
        </ResponsivePaper>

        {/* Footer */}
        <Box sx={{ mt: 1, textAlign: 'center', color: '#0f3460', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption">© 2024 Diagnostic Lab</Typography>
          <Chip size="small" label="Privacy" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Chip size="small" label="Terms" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Chip size="small" label="Contact" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        </Box>
      </Box>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onClose={() => setShowCamera(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Capture Prescription</Typography>
            <IconButton onClick={() => setShowCamera(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width="100%"
              videoConstraints={{ facingMode: 'environment' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCamera(false)}>Cancel</Button>
          <GradientButton onClick={handleCapture}>Capture</GradientButton>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onClose={() => setShowAddMemberDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Family Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Full Name *" value={newPatient.name} onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Age *" type="number" value={newPatient.age} onChange={(e) => setNewPatient({ ...newPatient, age: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Gender *</InputLabel>
                <Select value={newPatient.gender} onChange={(e: SelectChangeEvent) => setNewPatient({ ...newPatient, gender: e.target.value })}>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Relationship</InputLabel>
                <Select value={newFamilyMember.relationship} onChange={handleRelationshipChange}>
                  {['Self', 'Father', 'Mother', 'Husband', 'Wife', 'Son', 'Daughter', 'Other'].map(option => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddMemberDialog(false)}>Cancel</Button>
          <GradientButton onClick={() => {
            handleAddFamilyMember({
              CustomerName: newPatient.name,
              Age: parseInt(newPatient.age),
              Gender: newPatient.gender,
              MobileNumber: mobileNumber,
              Relationship: newFamilyMember.relationship,
            });
          }}>Add Member</GradientButton>
        </DialogActions>
      </Dialog>

      {/* Recent Registrations Dialog */}
      <RecentRegistrationsDialog
        open={showRecentRegistrations}
        onClose={() => setShowRecentRegistrations(false)}
        registrations={recentTempRegistrations}
        // In the RecentRegistrationsDialog onSelect handler
onSelect={(reg) => {
  setTempRegistration(reg);
  
  // Handle extracted data with proper type conversion
  if (reg.extractedData) {
    // Cast to any first to bypass type checking, then to the expected type
    const extractedData = reg.extractedData as any;
    
    // Transform to match ParsedPrescriptionData structure if needed
    const prescriptionData: ParsedPrescriptionData = {
      patientName: extractedData.patientName || reg.patientName,
      age: extractedData.age || reg.age,
      gender: extractedData.gender || reg.gender,
      mobileNumber: extractedData.mobileNumber || reg.mobileNumber,
      doctorName: extractedData.doctorName || reg.doctorName,
      date: extractedData.date,
      tests: extractedData.tests?.map((test: any) => ({
        name: test.name || test.testName,
        confidence: test.confidence || test.aiConfidence,
        matchedTest: test.matchedTest
      })) || [],
      confidence: extractedData.confidence,
      imageUrl: extractedData.imageUrl
    };
    
    setPrescriptionData(prescriptionData);
  }
  
  setNewPatient({
    name: reg.patientName || '',
    age: reg.age?.toString() || '',
    gender: reg.gender || 'Male',
    email: reg.email || '',
    doctorName: reg.doctorName || '',
  });
  
  setSelectedTests(reg.selectedTests || []);
  setSelectedTestIds(reg.selectedTests?.map(t => t.testId) || []);
  setShowRecentRegistrations(false);
  enqueueSnackbar(`Loaded registration: ${reg.registrationNo}`, { variant: 'success' });
}}
        onSendReminder={handleSendReminder}
      />
    </ResponsiveContainer>
  );
};